import { Loader, Text, Title } from "dash-styled"
import {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef
} from "react"
import { Table } from "resha"
import { DocumentExtraction } from "universal-schema/lib/1.0.0/document-schema"
import { useJobList } from "../../../../../../../../../Hooks/useJobList"
import { useJobResponse } from "../../../../../../../../../Hooks/useJobResponse"
import { useSignedUrl } from "../../../../../../../../../Hooks/useSignedUrl"
import {
    APIJobListParams,
    APIJobModel,
    JobOrderBy,
    JobSortBy,
    JobStatus
} from "../../../../../../../../../Models/API/JobModel"
import { hasValue } from "../../../../../../../../../Models/Client/FetchState"
import { InputFormData } from "../../../../../../../../Common/SchemaForm/DocumentsExtraction"
import { DocumentsExtractionContext } from "../../../../../../../../Common/SchemaForm/DocumentsExtraction/DocumentsExtractionContext"
import { Thumbnail } from "../../../../../../../../Common/Thumbnail/Thumbnail"

type DocumentSelectorProps = {
    appID: string
    selectedJobID?: string
    setSelectedJobID: Dispatch<SetStateAction<string | undefined>>
    setSelectedDocument: Dispatch<SetStateAction<string | undefined>>
}

const params: APIJobListParams = {
    orderBy: JobOrderBy.desc,
    sortBy: JobSortBy.completed
}

const Thumb = ({ input }: APIJobModel) => {
    const url = (input as InputFormData).documentUrl
    const signed = useSignedUrl(url)

    return hasValue(signed) ? (
        <Thumbnail
            type={url.includes(".pdf") ? "document" : "image"}
            value={signed.value}
        />
    ) : null
}

export const DocumentSelector = ({
    appID,
    selectedJobID,
    setSelectedJobID,
    setSelectedDocument
}: DocumentSelectorProps) => {
    const scrollableRef = useRef<HTMLDivElement>(null)
    const jobsListFetch = useJobList(appID, {
        params,
        refetchOnStale: true
    })
    const jobResponseFetch = useJobResponse(appID, selectedJobID)

    const { setAnnotationsOutputList, setJob } = useContext(
        DocumentsExtractionContext
    )

    useEffect(() => {
        if (hasValue(jobResponseFetch)) {
            const outputList = jobResponseFetch.value
                .response as DocumentExtraction["schema_instance"]
            if (outputList) {
                setAnnotationsOutputList(outputList)
                setSelectedDocument(outputList?.url)
            }
        }
    }, [jobResponseFetch, setAnnotationsOutputList, setSelectedDocument])

    const items = useMemo(() => {
        return hasValue(jobsListFetch)
            ? jobsListFetch.value.items.filter(
                  ({ status }) =>
                      status === JobStatus.COMPLETED ||
                      status === JobStatus.FAILED
              )
            : undefined
    }, [jobsListFetch])

    const handleClick = (job: APIJobModel) => {
        setJob(job)
        setSelectedJobID(job.id)
    }

    const isJobOutputLoading = Boolean(
        selectedJobID && !hasValue(jobResponseFetch)
    )

    return isJobOutputLoading ? (
        <Loader />
    ) : (
        <>
            <Title variant="heading2" component="h3" className="self-start">
                Choose document:
            </Title>
            <div ref={scrollableRef} className="w-full overflow-auto">
                <Table<APIJobModel>
                    columns={[
                        {
                            renderHeader: () => null,
                            render: Thumb,
                            className: "w-1/12"
                        },
                        {
                            renderHeader: () => "",
                            render: item => (
                                <Text
                                    className="m-0"
                                    weight="semibold"
                                    size="sm"
                                >
                                    {(item.input as InputFormData).documentUrl}
                                </Text>
                            ),
                            className: "w-11/12"
                        }
                    ]}
                    items={items}
                    getKey={item => item.id}
                    onRowClick={handleClick}
                    renderEmpty={() => <Text>No data</Text>}
                    className={`w-full h-0 min-h-0 mt-0`}
                    stickyHeader={true}
                />
            </div>
        </>
    )
}
